import { useResetOnClose } from "@/lib/useResetOnClose";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Download, Upload, PlayCircle, ChevronDown, ChevronRight, Pencil, Save, FolderPlus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { UomSelect } from "@/components/UomSelect";
import { TemplateRatesButton, ratesTotal } from "@/pages/setup/TemplateRates";

import { confirmAction } from "@/lib/dialogs";
const CATS = ["ENGINEERING", "PROCUREMENT", "CONSTRUCTION"];
const CAT_LABEL = { ENGINEERING: "Engineering", PROCUREMENT: "Procurement", CONSTRUCTION: "Construction", APPROVAL: "Approval" };
const DEFAULT_CAT_SPLIT = { ENGINEERING: "0.15", PROCUREMENT: "0.20", CONSTRUCTION: "0.60", APPROVAL: "0.05" };
const DEFAULT_SUB_SPLIT = { PO: "0.20", MFC: "0.10", DELIVERY: "0.70" };
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const fmt = (v) => (v === null || v === undefined || v === "" ? "" : String(Number(v)));

export default function WBSTemplates() {
  const { isOrgAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const importRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: p }] = await Promise.all([api.get("/wbs-templates"), api.get("/projects")]);
      setTemplates(t);
      setProjects(p);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const exportTemplate = async (template) => {
    try {
      const { data } = await api.get(`/wbs-templates/${template.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${template.name.replace(/[^a-z0-9]+/gi, "_")}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const payload = JSON.parse(await file.text());
      await api.post("/wbs-templates/import", payload);
      toast.success("Template imported");
      load();
    } catch (err) {
      toast.error(err instanceof SyntaxError ? "That file is not valid JSON" : getErrorMessage(err));
    }
  };

  const remove = async (template) => {
    if (!await confirmAction(`Delete template "${template.name}"? Projects already built from it are not affected.`)) return;
    try {
      await api.delete(`/wbs-templates/${template.id}`);
      toast.success("Template deleted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="wbs-templates-page">
      <PageHeader
        title="Setup Library"
        description="Build the Engineering, Procurement and Construction structure once. Choose it when you create a project and the WBS, quantities, weights and DPR rows are set up automatically (solar templates also scale quantities by DC MWp)."
        action={isOrgAdmin && (
          <div className="flex flex-wrap gap-2">
            <input ref={importRef} type="file" accept="application/json" className="hidden" onChange={importFile} />
            <Button variant="outline" size="sm" onClick={() => importRef.current?.click()} data-testid="import-template-button"><Upload className="h-3.5 w-3.5" /> Import</Button>
            <SaveFromProjectDialog projects={projects} onSaved={load} />
            <TemplateEditorDialog onSaved={load} />
          </div>
        )}
      />

      {!loading && templates.length === 0 ? (
        <EmptyState title="No templates yet" description="Create one, or save an existing project's WBS as a template." testId="templates-empty-state" />
      ) : (
        <div className="space-y-3">
          {templates.map((t) => {
            const counts = CATS.map((c) => [c, t.items.filter((i) => i.category === c && !i.is_group).length]);
            return (
              <div key={t.id} className="rounded-xl border border-border bg-card" data-testid={`template-card-${t.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <button type="button" className="flex min-w-0 items-start gap-2 text-left" onClick={() => setExpanded({ ...expanded, [t.id]: !expanded[t.id] })}>
                    {expanded[t.id] ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0" /> : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0" />}
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 font-medium text-foreground">{t.name} {t.is_system && <Badge variant="secondary">Built-in</Badge>}</p>
                      {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {counts.map(([c, n]) => `${CAT_LABEL[c]} ${n}`).join(" · ")} activities
                        {t.category_split && ` · split ${Object.entries(t.category_split).map(([k, v]) => `${CAT_LABEL[k]?.[0] || k[0]} ${Math.round(Number(v) * 100)}%`).join(" / ")}`}
                      </p>
                    </div>
                  </button>
                  <div className="flex flex-wrap gap-1.5">
                    {isOrgAdmin && <TemplateEditorDialog template={t} onSaved={load} />}
                    <Button size="sm" variant="ghost" onClick={() => exportTemplate(t)} data-testid={`export-template-button-${t.id}`}><Download className="h-3.5 w-3.5" /> Export</Button>
                    <ApplyTemplateDialog template={t} projects={projects} />
                    {isOrgAdmin && !t.is_system && (
                      <Button size="sm" variant="ghost" onClick={() => remove(t)} data-testid={`delete-template-button-${t.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>
                    )}
                  </div>
                </div>
                {expanded[t.id] && <TemplatePreview template={t} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TemplatePreview({ template }) {
  return (
    <div className="space-y-4 border-t border-border p-4">
      {CATS.map((cat) => {
        const rows = template.items.filter((i) => i.category === cat).sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code, undefined, { numeric: true }));
        if (!rows.length) return null;
        const groups = rows.filter((r) => r.is_group);
        const ordered = [...groups.flatMap((g) => [g, ...rows.filter((r) => !r.is_group && r.parent_code === g.code)]),
          ...rows.filter((r) => !r.is_group && !groups.some((g) => g.code === r.parent_code))];
        return (
          <div key={cat}>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{CAT_LABEL[cat]}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground">
                  <tr><th className="py-1 text-left font-medium">Code</th><th className="text-left font-medium">Name</th><th className="text-left font-medium">UoM</th><th className="text-right font-medium">Qty / MWp</th><th className="text-right font-medium">Fixed qty</th><th className="text-right font-medium">Weight</th><th className="text-right font-medium">Cost / unit</th></tr>
                </thead>
                <tbody>
                  {ordered.map((r) => (
                    <tr key={r.id} className="border-t border-border/60">
                      <td className={`py-1 font-mono text-xs ${r.is_group ? "font-semibold" : "pl-3"}`}>{r.code}</td>
                      <td className={r.is_group ? "font-semibold text-foreground" : "text-foreground"}>{r.name}{r.is_critical && <span className="ml-1.5 text-[10px] text-rose-500">critical</span>}</td>
                      <td className="text-xs text-muted-foreground">{r.uom || ""}</td>
                      <td className="text-right font-mono text-xs">{fmt(r.quantity_per_mwp)}</td>
                      <td className="text-right font-mono text-xs">{fmt(r.quantity)}</td>
                      <td className="text-right font-mono text-xs">{r.weight != null ? `${(Number(r.weight) * 100).toFixed(1)}%` : ""}</td>
                      <td className="text-right font-mono text-xs">{ratesTotal(r.cost_lines) ? `₹${ratesTotal(r.cost_lines).toLocaleString("en-IN")}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function blankItem(category, isGroup, sort) {
  return { category, code: "", name: "", sort_order: sort, is_group: isGroup, parent_code: "", uom: "", quantity_per_mwp: "", quantity: "", weight: "", is_critical: false, cost_lines: [] };
}

function TemplateEditorDialog({ template, onSaved }) {
  const editing = Boolean(template);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("CONSTRUCTION");
  const [form, setForm] = useState({ name: "", description: "" });
  const [catSplit, setCatSplit] = useState(DEFAULT_CAT_SPLIT);
  const [subSplit, setSubSplit] = useState(DEFAULT_SUB_SPLIT);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({ name: "", description: "" }); setCatSplit(DEFAULT_CAT_SPLIT); setSubSplit(DEFAULT_SUB_SPLIT); });      // closed without saving: opens fresh next time

  useEffect(() => {
    if (!open) return;
    if (template) {
      setForm({ name: template.name, description: template.description || "" });
      setCatSplit(Object.fromEntries(Object.entries({ ...DEFAULT_CAT_SPLIT, ...(template.category_split || {}) }).map(([k, v]) => [k, fmt(v)])));
      setSubSplit(Object.fromEntries(Object.entries({ ...DEFAULT_SUB_SPLIT, ...(template.sub_stage_split || {}) }).map(([k, v]) => [k, fmt(v)])));
      setItems(template.items.map((i) => ({
        category: i.category, code: i.code, name: i.name, sort_order: i.sort_order, is_group: i.is_group, parent_code: i.parent_code || "",
        uom: i.uom || "", quantity_per_mwp: fmt(i.quantity_per_mwp), quantity: fmt(i.quantity), weight: fmt(i.weight), is_critical: i.is_critical,
        cost_lines: (i.cost_lines || []).map((c) => ({ category: c.category, description: c.description, rate: fmt(c.rate) })),
      })));
    } else {
      setForm({ name: "", description: "" });
      setCatSplit(DEFAULT_CAT_SPLIT);
      setSubSplit(DEFAULT_SUB_SPLIT);
      setItems([]);
    }
  }, [open, template]);

  const rows = items.map((it, idx) => ({ ...it, idx })).filter((it) => it.category === tab);
  const groupCodes = items.filter((i) => i.category === tab && i.is_group && i.code).map((i) => i.code);
  const weightSum = items.filter((i) => i.category === tab && !i.is_group).reduce((s, i) => s + (Number(i.weight) || 0), 0);
  const hasWeights = items.some((i) => i.category === tab && !i.is_group && i.weight !== "");
  const catSum = Object.values(catSplit).reduce((s, v) => s + (Number(v) || 0), 0);
  const subSum = Object.values(subSplit).reduce((s, v) => s + (Number(v) || 0), 0);

  const update = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  const add = (isGroup) => {
    const inTab = items.filter((i) => i.category === tab);
    setItems([...items, { ...blankItem(tab, isGroup, inTab.length + 1), parent_code: isGroup ? "" : groupCodes[groupCodes.length - 1] || "" }]);
  };

  const submit = async (e) => {
    e.preventDefault();
    const clean = items.filter((i) => i.code.trim() && i.name.trim()).map((i) => ({
      category: i.category, code: i.code.trim(), name: i.name.trim(), sort_order: Number(i.sort_order) || 0,
      is_group: i.is_group, parent_code: i.is_group ? null : i.parent_code || null, uom: i.is_group ? null : i.uom || null,
      quantity_per_mwp: i.is_group ? null : num(i.quantity_per_mwp), quantity: i.is_group ? null : num(i.quantity),
      weight: i.is_group ? null : num(i.weight), is_critical: Boolean(i.is_critical),
      cost_lines: i.is_group ? [] : (i.cost_lines || []).filter((c) => (c.description || "").trim()).map((c) => ({ category: c.category, description: c.description.trim(), rate: Number(c.rate) || 0 })),
    }));
    if (!clean.length) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form, description: form.description || null,
        category_split: Object.fromEntries(Object.entries(catSplit).map(([k, v]) => [k, Number(v) || 0])),
        sub_stage_split: Object.fromEntries(Object.entries(subSplit).map(([k, v]) => [k, Number(v) || 0])),
        items: clean,
      };
      if (editing) await api.put(`/wbs-templates/${template.id}`, payload);
      else await api.post("/wbs-templates", payload);
      toast.success(editing ? "Template updated" : "Template created");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing
          ? <Button size="sm" variant="ghost" data-testid={`edit-template-button-${template.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
          : <Button size="sm" data-testid="create-template-button"><Plus className="h-4 w-4" /> New Template</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${template.name}` : "New WBS Template"}</DialogTitle>
          <DialogDescription>Changes apply to projects created from now on. Existing projects keep their own WBS.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="template-editor-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="template-name-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="template-description-input" /></div>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs font-semibold text-foreground">Category split <span className={`font-normal ${Math.abs(catSum - 1) < 0.001 ? "text-emerald-600" : "text-rose-500"}`}>(total {(catSum * 100).toFixed(0)}%)</span></p>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {Object.keys(DEFAULT_CAT_SPLIT).map((k) => (
                  <div key={k}><Label className="text-[11px]">{CAT_LABEL[k]}</Label><Input type="number" step="0.01" min="0" max="1" className="mt-1 h-8" value={catSplit[k] ?? ""} onChange={(e) => setCatSplit({ ...catSplit, [k]: e.target.value })} /></div>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs font-semibold text-foreground">Procurement stages <span className={`font-normal ${Math.abs(subSum - 1) < 0.001 ? "text-emerald-600" : "text-rose-500"}`}>(total {(subSum * 100).toFixed(0)}%)</span></p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {Object.keys(DEFAULT_SUB_SPLIT).map((k) => (
                  <div key={k}><Label className="text-[11px]">{k === "MFC" ? "MFC" : k.charAt(0) + k.slice(1).toLowerCase()}</Label><Input type="number" step="0.01" min="0" max="1" className="mt-1 h-8" value={subSplit[k] ?? ""} onChange={(e) => setSubSplit({ ...subSplit, [k]: e.target.value })} /></div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              {CATS.map((c) => (
                <Button key={c} type="button" size="sm" variant={tab === c ? "default" : "outline"} onClick={() => setTab(c)} data-testid={`template-tab-${c}`}>
                  {CAT_LABEL[c]} ({items.filter((i) => i.category === c && !i.is_group).length})
                </Button>
              ))}
              <span className={`ml-auto text-xs ${!hasWeights || Math.abs(weightSum - 1) < 0.001 ? "text-muted-foreground" : "text-amber-600 dark:text-amber-400"}`}>
                {hasWeights ? `Weights in ${CAT_LABEL[tab]}: ${(weightSum * 100).toFixed(1)}% (should be 100%)` : "No weights — activities share equally"}
              </span>
            </div>
            <div className="mt-2 overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[900px] text-xs">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left font-medium">Group</th><th className="p-2 text-left font-medium">Code</th><th className="p-2 text-left font-medium">Under group</th>
                    <th className="p-2 text-left font-medium">Name</th><th className="p-2 text-left font-medium">UoM</th><th className="p-2 text-left font-medium">Qty per MWp</th>
                    <th className="p-2 text-left font-medium">or Fixed qty</th><th className="p-2 text-left font-medium">Weight (0–1)</th><th className="p-2 text-left font-medium">Critical</th><th className="p-2 text-left font-medium">Cost / unit</th><th />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={11} className="p-3 text-center text-muted-foreground">No {CAT_LABEL[tab]} items yet — add a group header, then activities under it.</td></tr>}
                  {rows.map((it) => (
                    <tr key={it.idx} className={`border-t border-border ${it.is_group ? "bg-muted/30" : ""}`}>
                      <td className="p-1.5"><Switch checked={it.is_group} onCheckedChange={(v) => update(it.idx, "is_group", v)} /></td>
                      <td className="p-1.5"><Input required className="h-8 w-20 font-mono" value={it.code} onChange={(e) => update(it.idx, "code", e.target.value)} /></td>
                      <td className="p-1.5">
                        {it.is_group ? <span className="text-muted-foreground">—</span> : (
                          <Select value={it.parent_code || "none"} onValueChange={(v) => update(it.idx, "parent_code", v === "none" ? "" : v)}>
                            <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="none">None</SelectItem>{groupCodes.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                      </td>
                      <td className="p-1.5"><Input required className={`h-8 min-w-[12rem] ${it.is_group ? "font-semibold" : ""}`} value={it.name} onChange={(e) => update(it.idx, "name", e.target.value)} /></td>
                      <td className="p-1.5">{!it.is_group && <UomSelect value={it.uom} onChange={(v) => update(it.idx, "uom", v)} className="h-8 w-28" />}</td>
                      <td className="p-1.5">{!it.is_group && <Input type="number" step="0.0001" min="0" className="h-8 w-24" value={it.quantity_per_mwp} onChange={(e) => update(it.idx, "quantity_per_mwp", e.target.value)} />}</td>
                      <td className="p-1.5">{!it.is_group && <Input type="number" step="0.001" min="0" className="h-8 w-20" value={it.quantity} onChange={(e) => update(it.idx, "quantity", e.target.value)} />}</td>
                      <td className="p-1.5">{!it.is_group && <Input type="number" step="0.001" min="0" max="1" className="h-8 w-20" value={it.weight} onChange={(e) => update(it.idx, "weight", e.target.value)} />}</td>
                      <td className="p-1.5">{!it.is_group && <Switch checked={it.is_critical} onCheckedChange={(v) => update(it.idx, "is_critical", v)} />}</td>
                      <td className="p-1.5">{!it.is_group && <TemplateRatesButton lines={it.cost_lines || []} onChange={(v) => update(it.idx, "cost_lines", v)} />}</td>
                      <td className="p-1.5"><Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== it.idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => add(true)} data-testid="add-template-group-button"><FolderPlus className="h-3.5 w-3.5" /> Add group header</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => add(false)} data-testid="add-template-item-button"><Plus className="h-3.5 w-3.5" /> Add activity</Button>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              <b>Qty per MWp</b> is multiplied by the project's DC capacity (e.g. 700 piles/MWp × 5 MWp = 3,500). Use <b>Fixed qty</b> for things that don't scale (e.g. 1 transformer yard).
            </p>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="template-save-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Create template"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ApplyTemplateDialog({ template, projects }) {
  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [merge, setMerge] = useState(false);
  const [applying, setApplying] = useState(false);
  useResetOnClose(Boolean(open), () => { setProjectId(""); });      // closed without saving: opens fresh next time
  const project = projects.find((p) => p.id === projectId);

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/wbs-templates/${template.id}/apply`, {}, { params: { merge } });
      toast.success(
        `${data.created_items} item(s) added, ${data.skipped_existing} already existed, ${data.dpr_rows_added} DPR row(s) created`
        + (data.scaled_by_mwp ? ` · quantities scaled for ${Number(data.scaled_by_mwp)} MWp` : " · no DC capacity set, per-MWp quantities left blank")
        + (data.estimate_version ? ` · budget copied: draft estimate v${data.estimate_version} (₹${Number(data.estimate_total).toLocaleString("en-IN")}) created in Estimates & Budgets - approve it there`
          : data.cost_lines_added ? " · cost lines copied, but no quantity yet, so no estimate was created" : ""),
      );
      setOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid={`apply-template-button-${template.id}`}><PlayCircle className="h-3.5 w-3.5" /> Apply</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Apply "{template.name}"</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Project</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="mt-1.5" data-testid="apply-template-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
            {project && <p className="mt-1 text-[11px] text-muted-foreground">DC capacity: {project.dc_capacity_mwp ? `${Number(project.dc_capacity_mwp)} MWp` : "not set — per-MWp quantities will be blank"}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={merge} onCheckedChange={setMerge} data-testid="apply-template-merge-switch" />
            <Label>Merge — add only missing items (needed if the project already has a WBS; also repairs missing DPR rows)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={!projectId || applying} onClick={apply} data-testid="apply-template-confirm-button">{applying && <Loader2 className="h-4 w-4 animate-spin" />} Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SaveFromProjectDialog({ projects, onSaved }) {
  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setProjectId(""); setForm({ name: "", description: "" }); });      // closed without saving: opens fresh next time

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/wbs-templates/save`, { name: form.name, description: form.description || null });
      toast.success("Project WBS saved as a template");
      setOpen(false);
      setForm({ name: "", description: "" });
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="save-project-as-template-button"><Save className="h-3.5 w-3.5" /> Save a project as template</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save a project's WBS as a template</DialogTitle>
          <DialogDescription>Copies the structure, units, weights and splits. Quantities are stored per MWp (if the project has a DC capacity), so they scale to the next project.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label>Project</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Template name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={!projectId || saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save template</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
